import React from 'react';
import { Target, Globe, Shield, Users, Award, Clock, TrendingUp } from 'lucide-react';

const About = () => {
  const stats = [
    { icon: Clock, value: "2022", label: "Founded", color: "from-blue-400 to-cyan-500" },
    { icon: Users, value: "2-10", label: "Expert Team", color: "from-green-400 to-emerald-500" },
    { icon: Award, value: "50+", label: "Projects Delivered", color: "from-purple-400 to-pink-500" },
    { icon: TrendingUp, value: "100%", label: "Client Satisfaction", color: "from-orange-400 to-red-500" }
  ];

  const values = [
    {
      icon: Target,
      title: "Our Mission",
      description: "To empower businesses with intelligent technology solutions that drive growth and innovation",
      details: "We believe in transforming ideas into reality through cutting-edge technology and strategic thinking."
    },
    {
      icon: Globe,
      title: "Our Vision",
      description: "To be the leading IT consulting partner for digital transformation across India",
      details: "We envision a future where every business leverages technology to achieve unprecedented success."
    },
    {
      icon: Shield,
      title: "Our Values",
      description: "Innovation, integrity, and excellence in every project we undertake",
      details: "We maintain the highest standards of quality while fostering a culture of continuous learning."
    }
  ];

  const services = [
    "Custom Web Development",
    "Mobile App Development",
    "Cloud Infrastructure",
    "Digital Transformation",
    "DevOps & Automation",
    "UI/UX Design"
  ];

  return (
    <section id="about" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header Section */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            About Algoryn
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            Founded in 2022, Algoryn has emerged as a trusted IT consulting partner, 
            helping businesses across India harness the power of technology to achieve 
            their digital transformation goals.
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-cyan-400 to-blue-500 mx-auto rounded-full"></div>
        </div>
        
        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${stat.color} text-white mb-4`}>
                <stat.icon className="w-8 h-8" />
              </div>
              <div className="text-3xl font-bold text-slate-900 mb-2">{stat.value}</div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
        
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Story Section */}
          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-bold text-slate-900 mb-6">Our Story</h3>
              <div className="space-y-6">
                <p className="text-gray-600 leading-relaxed">
                  At Algoryn, we believe that every business challenge can be solved with the right algorithm. 
                  Our journey began with a simple yet powerful vision: to bridge the gap between complex 
                  technology and business success.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Our team of skilled professionals combines deep technical expertise with strategic business 
                  acumen to deliver solutions that are not just functional, but truly transformational. We 
                  understand that in today's digital-first world, technology is not just a tool—it's a 
                  strategic advantage.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Whether you're a startup launching your first product, a growing company scaling your 
                  operations, or an established enterprise optimizing your digital infrastructure, we're 
                  your trusted partner in building secure, scalable, and high-performance systems.
                </p>
              </div>
            </div>
            
            {/* Services Grid */}
            <div>
              <h4 className="text-xl font-bold text-slate-900 mb-4">Our Expertise</h4>
              <div className="grid grid-cols-2 gap-3">
                {services.map((service, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full"></div>
                    <span className="text-gray-700 text-sm">{service}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Values and Location Section */}
          <div className="space-y-8">
            {/* Values */}
            <div>
              <h3 className="text-3xl font-bold text-slate-900 mb-8">Our Foundation</h3>
              <div className="space-y-6">
                {values.map((item, index) => (
                  <div key={index} className="bg-slate-50 rounded-xl p-6 hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-r from-cyan-400 to-blue-500 p-3 rounded-lg flex-shrink-0">
                        <item.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-slate-900 mb-2">{item.title}</h4>
                        <p className="text-gray-600 text-sm mb-3">{item.description}</p>
                        <p className="text-gray-500 text-xs">{item.details}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Location Card
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 text-white">
              <div className="flex items-center space-x-4 mb-6">
                <div className="bg-gradient-to-r from-cyan-400 to-blue-500 p-3 rounded-lg">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-xl">Our Location</h4>
                  <p className="text-gray-300 text-sm">Serving clients across India</p>
                </div>
              </div>
              
              <div className="space-y-2 text-gray-300">
                <p className="font-semibold text-white">Headquarters:</p>
                <p>Phase 1, Akash Nagar</p>
                <p>Rasulpur Sikroda</p>
                <p>Ghaziabad, 201015</p>
                <p>Uttar Pradesh, India</p>
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-700">
                <p className="text-sm text-gray-400">
                  Strategically located in the NCR region, we're positioned to serve 
                  businesses across North India while maintaining global connectivity.
                </p>
              </div>
            </div> */}
          </div>
        </div>
        
        {/* Why Choose Us Section */}
        <div className="mt-20 bg-gradient-to-r from-slate-50 to-blue-50 rounded-3xl p-12">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold text-slate-900 mb-6">Why Choose Algoryn?</h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2">Expert Team</h4>
                <p className="text-gray-600 text-sm">
                  Skilled professionals with deep expertise in modern technologies and industry best practices.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2">Proven Results</h4>
                <p className="text-gray-600 text-sm">
                  Track record of successful project delivery with 100% client satisfaction rate.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-white" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2">Growth Focused</h4>
                <p className="text-gray-600 text-sm">
                  Solutions designed to scale with your business and drive sustainable growth.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;